import React, { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { Alert, Button, FormFeedback, FormGroup, FormText, Input, Label, ModalBody, Table } from "reactstrap"
import { get } from "helpers/api_helper"
import DateTimeField from "components/Common/DateTimeField"
import { todayIn } from "pages/Logistics/Stock/stockForm"
import { actorName, logTime, plural, quantityLabel } from "./labels"
import DraftModal from "./DraftModal"
import ParcelSelector, { documentsOnly, initialSelection, parcelWeights, selectionCheck } from "./ParcelSelector"
import { AfterShipmentDialog, ReasonField } from "./AfterShipmentDialogs"
import { blockerText, carriedItems, courierNames, itemKey, itemName, recipientName, reasonHint, sentItems, shipmentName, transportLabel, transportName,
  warningText, weightLabel } from "./shipmentLabels"
import { AWB, httpsLink } from "./TransportFields"
import { logisticsMessage } from "./useLogisticsOperation"

// ——— The warehouse's steps on a shipment, the main shipment of a validated sheet or a supplementary one (D14–D19; CONTEXT „Procesarea”,
// „Marcarea ca expediată”, „Pașii înapoi ai fișei”, „Expediere suplimentară”) ———

// The note the API takes with a stated transport (common/logistics): at most 500 characters.
const NOTE_LIMIT = 500
// "Andrei Popa (contact, organizer) receives": who a shipment's email goes to, as its email of now names them.
const receivers = shipment => {
  const recipients = shipment.email?.recipients || []
  if (!recipients.length) return null
  return `${recipients.map(recipientName).join(", ")} ${recipients.length === 1 ? "receives" : "receive"}`
}
// What an AWB field says is wrong with it: an AWB as printed, or, when `required`, one at all.
const awbProblem = (value, required) => {
  const awb = value.trim()
  if (!awb) return required ? "Enter the AWB." : null
  return AWB.test(awb) ? null : logisticsMessage("INVALID_LOGISTICS_AWB")
}
// DPD's or another courier's tracking link, optional: an https link.
const linkProblem = value => (value.trim() && !httpsLink(value.trim()) ? "Paste an https link, or leave it empty." : null)
// What the AWB field holds, trimmed, with DPD's or another courier's link when one is pasted.
const awbBody = (courier, awb, link) => ({ awb: awb.trim(), ...(courier !== "cargus" && link.trim() ? { trackingUrl: link.trim() } : {}) })
// Where a shipment stands before its processing: the main one's sheet ready for processing, a supplementary one in preparation (D19).
const unprocessedName = shipment => (shipment.kind === "supplementary" ? "In preparation" : "Ready for processing")

// The AWB and DPD's or another courier's tracking link, entered by hand.
function AwbFields({ courier, id, label, awb, link, onAwb, onLink, required, prompt }) {
  const problem = awb.trim() || !required ? awbProblem(awb, required) : null
  return <>
    <FormGroup>
      <Label for={`${id}-awb`}>{label}{required && <span className="text-danger"> *</span>}</Label>
      <Input id={`${id}-awb`} value={awb} invalid={Boolean(problem)} onChange={event => onAwb(event.target.value)} />
      {problem && <FormFeedback>{problem}</FormFeedback>}
      <FormText>Created in the courier's application or with the AI prompt.{prompt && <> <Button color="link" size="sm" className="p-0 align-baseline"
        onClick={prompt}>Generate AI prompt</Button></>}</FormText>
    </FormGroup>
    {courier !== "cargus" && <FormGroup>
      <Label for={`${id}-link`}>Tracking link</Label>
      <Input id={`${id}-link`} value={link} invalid={Boolean(linkProblem(link))} onChange={event => onLink(event.target.value)} />
      {linkProblem(link) && <FormFeedback>{linkProblem(link)}</FormFeedback>}
    </FormGroup>}
  </>
}

// How a supplementary shipment travels, stated at its first processing, as the sheet's validation states the main shipment's (D19): with a
// courier, Cargus, DPD or another one named, and an optional note; without one, the note the club and the organizer see, required.
function TransportChoice({ travel, onChange }) {
  const set = fields => onChange({ ...travel, ...fields })
  return <>
    <FormGroup tag="fieldset">
      <legend className="col-form-label pt-0">Transport</legend>
      {[["courier", "With a courier"], ["noCourier", "Without courier"]].map(([transport, label]) => <FormGroup check inline key={transport}>
        <Input id={`logistics-process-transport-${transport}`} type="radio" name="logistics-process-transport" checked={travel.transport === transport}
          onChange={() => set({ transport })} />
        <Label check for={`logistics-process-transport-${transport}`}>{label}</Label>
      </FormGroup>)}
    </FormGroup>
    {travel.transport === "courier" && <div className="form-row">
      <FormGroup className="col-sm-4">
        <Label for="logistics-process-courier">Courier</Label>
        <Input id="logistics-process-courier" type="select" value={travel.courier} onChange={event => set({ courier: event.target.value })}>
          {Object.entries(courierNames).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </Input>
      </FormGroup>
      {travel.courier === "other" && <FormGroup className="col-sm-8">
        <Label for="logistics-process-courier-name">Courier name</Label>
        <Input id="logistics-process-courier-name" maxLength={60} value={travel.courierName} onChange={event => set({ courierName: event.target.value })}
          placeholder="e.g. Speedy" />
      </FormGroup>}
    </div>}
    <FormGroup>
      <Label for="logistics-process-note">Note{travel.transport === "courier" ? " (optional)" : <span className="text-danger"> *</span>}</Label>
      <Input id="logistics-process-note" type="textarea" rows={2} maxLength={NOTE_LIMIT} value={travel.note} onChange={event => set({ note: event.target.value })}
        placeholder="e.g. taken by the referee from Câmpina" />
      <FormText>The club and the organizer will see this note.</FormText>
    </FormGroup>
  </>
}
// Whether a stated transport says all it needs: another courier's name, or the note without a courier; and how the command states it.
const travelComplete = travel => (travel.transport === "courier" ? travel.courier !== "other" || Boolean(travel.courierName.trim()) : Boolean(travel.note.trim()))
const travelBody = ({ transport, courier, courierName, note }) => (transport === "courier"
  ? { transport, courier, ...(courier === "other" ? { courierName: courierName.trim() } : {}), ...(note.trim() ? { note: note.trim() } : {}) }
  : { transport, note: note.trim() })

// What a processing emails (D18–D19): a supplementary shipment's own email at its first processing since it was created or cancelled, with
// its content and its AWB or that it is handed over; otherwise the short AWB email when the AWB is new to the organizer.
const processingEmail = ({ shipment, withCourier, courier, needsAwb }) => {
  const who = receivers(shipment) || "The contact person and the organizer receive"
  if (shipment.kind === "supplementary" && !shipment.notifiedAt) {
    return `${who} the supplementary shipment's email: its content, ${withCourier ? `the AWB${courier === "cargus" ? " with the tracking link" : ""}`
      : "that the items are handed over without a courier"}, and what the club provides.`
  }
  if (!withCourier) return shipment.kind === "supplementary" ? "The organizer already has the supplementary shipment's email: no email." : null
  return needsAwb || shipment.awb !== shipment.notifiedAwb ? `The AWB is new: ${who.replace(/^The/, "the")} the short AWB email${courier === "cargus"
    ? " with the tracking link" : ""}.` : "The organizer already has this AWB: no email."
}

// „Validate processing” (D14, D19): the warehouse confirms the shipment is ready. With a courier it chooses the parcels, as for an AWB
// (ParcelSelector, their weights starting from the products and the packages, or from the weight set after weighing; after an undone
// processing, from the parcels chosen then), and enters the AWB the shipment does not hold yet; without a courier it only confirms. An AWB
// Cargus generated keeps the parcels it was generated with, so its processing undone is processed again without choosing them. A
// supplementary shipment, which no sheet's validation stated, states its transport at its first processing, and what blocks it on its own
// address and contact keeps the step disabled (its preview). The stock does not move until the shipment leaves.
export function ValidateProcessingDialog({ eventId, shipment, timezone, pending, failure, onSubmit, onPrompt, onClose }) {
  const supplementary = shipment.kind === "supplementary"
  const stating = supplementary && !shipment.transport
  const [travel, setTravel] = useState({ transport: "courier", courier: "cargus", courierName: "", note: "" })
  const transport = stating ? travel.transport : shipment.transport
  const courier = stating ? travel.courier : shipment.courier
  const withCourier = transport === "courier"
  const generated = shipment.awbSource === "generated"
  const choosing = withCourier && !generated
  const [read, setRead] = useState({ loading: choosing })
  const [preview, setPreview] = useState({ loading: supplementary })
  const [attempt, setAttempt] = useState(0)
  const [selection, setSelection] = useState(null)
  const [awb, setAwb] = useState("")
  const [link, setLink] = useState("")
  const shipmentId = shipment.id
  useEffect(() => {
    if (!choosing) return undefined
    let active = true
    setRead(previous => ({ ...previous, loading: true, error: false }))
    get("/admin/logistics/settings").then(response => {
      if (!active) return
      const { packages } = response.data
      setRead({ packages })
      setSelection(previous => previous || (shipment.packing?.length
        ? { parcels: shipment.packing.map(({ packageId, weight }) => ({ packageId, weight: String(weight) })), saturday: false }
        : initialSelection(packages, { weights: parcelWeights(shipment), documents: documentsOnly(shipment) })))
    }).catch(() => { if (active) setRead({ error: true }) })
    return () => { active = false }
  }, [choosing, attempt, shipment])
  // A supplementary shipment's own address and contact may block the transport: its preview says what.
  useEffect(() => {
    if (!supplementary) return undefined
    let active = true
    setPreview(previous => ({ ...previous, loading: true, error: false }))
    get(`/admin/logistics/events/${eventId}/shipments/validation`, { params: { shipment: shipmentId } }).then(response => {
      if (active) setPreview({ data: response.data })
    }).catch(() => { if (active) setPreview({ error: true }) })
    return () => { active = false }
  }, [supplementary, eventId, shipmentId, attempt])

  const packages = read.packages || []
  const blockers = preview.data?.blockers?.[transport] || []
  const warnings = preview.data?.warnings?.[transport] || []
  const needsAwb = withCourier && !shipment.awb
  const parcelsReady = !choosing || (selection?.parcels.length > 0 && selectionCheck(selection, packages).valid)
  const ready = (!supplementary || (preview.data && !blockers.length)) && (!stating || travelComplete(travel)) && parcelsReady
    && (!needsAwb || (!awbProblem(awb, true) && !linkProblem(link)))
  const email = processingEmail({ shipment, withCourier, courier, needsAwb })
  const submit = () => onSubmit({ action: "validateProcessing", ...(stating ? travelBody(travel) : {}), ...(choosing ? { parcels: selection.parcels.map(({ packageId,
    weight }) => ({ packageId, weight: Number(weight) })) } : {}), ...(needsAwb ? awbBody(courier, awb, link) : {}) })
  const retry = <Button color="link" className="p-0 align-baseline" onClick={() => setAttempt(value => value + 1)}>Retry</Button>

  return <DraftModal id="logistics-process-title" title={`Validate processing — ${shipmentName(shipment)}`} size="lg"
    dirty={Boolean(awb.trim() || link.trim() || travel.note.trim())} onClose={onClose}
    actions={<Button color="primary" disabled={pending || !ready} onClick={submit}>{pending ? "Validating…" : "Validate processing"}</Button>}>
    <ModalBody>
      <p className="small text-muted">{supplementary ? carriedItems(shipment).join(" · ") : [`Sheet validated by ${actorName(shipment.sheetValidatedBy)}`,
        logTime(shipment.sheetValidatedAt, timezone), transportName(shipment)].filter(Boolean).join(" · ")}</p>
      {preview.error && <Alert color="danger">The shipment could not be read. {retry}</Alert>}
      {stating && <TransportChoice travel={travel} onChange={setTravel} />}
      {supplementary && !stating && <p className="small">{transportLabel(shipment)}</p>}
      {blockers.length > 0 && <Alert color="danger" className="small">
        <p className="mb-1">{withCourier ? "A courier cannot take this shipment yet:" : "This shipment cannot be processed yet:"}</p>
        <ul className="mb-0">{blockers.map(blocker => <li key={blocker.kind}>{blockerText(blocker)}</li>)}</ul>
      </Alert>}
      {warnings.map(warning => <Alert key={warning.kind} color="warning" className="small py-2">{warningText(warning)}</Alert>)}
      {choosing && read.loading && !read.packages && <p role="status">Reading the packages…</p>}
      {read.error && <Alert color="danger">The packages could not be read. {retry}</Alert>}
      {choosing && read.packages && (selection?.parcels.length ? <ParcelSelector packages={packages} selection={selection} onChange={setSelection}
        weights={parcelWeights(shipment)} documents={documentsOnly(shipment)} saturday={false} />
        : <Alert color="warning" className="small">No shipping package is active. Add one in <Link to="/logistics/settings">Logistics → Settings</Link>.</Alert>)}
      {needsAwb && <AwbFields courier={courier} id="logistics-process" label="AWB" awb={awb} link={link} onAwb={setAwb} onLink={setLink} required
        prompt={onPrompt} />}
      {withCourier && shipment.awb && <p className="small">{generated ? `AWB ${shipment.awb}, generated through Cargus with ${plural(shipment.parcels, "parcel")}: they stay. `
        + "To change them, generate another AWB." : `AWB ${shipment.awb}: change it with Change AWB if it is wrong.`}</p>}
      <ul className="small mb-0 mt-2">
        {!withCourier && !supplementary && <li>The shipment is ready to be handed over without a courier. No email.</li>}
        {email && <li>{email}</li>}
        {withCourier && <li>{supplementary ? "SportyaOS shows it, ready for the courier, with the AWB."
          : "SportyaOS shows „Coletul e pregătit și așteaptă curierul”, with the AWB."}</li>}
        {!withCourier && supplementary && <li>SportyaOS shows it, ready to be handed over.</li>}
        <li>The stock does not change now: it decreases when the shipment leaves.</li>
      </ul>
      {failure && <Alert color="danger" className="mt-3 mb-0">{failure}</Alert>}
    </ModalBody>
  </DraftModal>
}

// „Add AWB” / „Change AWB” (D16): a shipment's AWB, until it leaves, with a courier. The first one is added without a reason; another one
// replaces it with a reason, and the AWB email goes then only when the organizer knew the one it replaces (`notifiedAwb`).
export function ChangeAwbDialog({ shipment, pending, failure, onSubmit, onClose }) {
  const [awb, setAwb] = useState("")
  const [link, setLink] = useState("")
  const [reason, setReason] = useState("")
  const changing = Boolean(shipment.awb)
  const known = shipment.notifiedAwb
  const typed = awb.trim()
  const ready = !awbProblem(awb, true) && !linkProblem(link) && (!changing || reason.trim())
  const submit = () => onSubmit({ action: "changeAwb", ...awbBody(shipment.courier, awb, link), ...(reason.trim() ? { reason: reason.trim() } : {}) })
  const title = `${changing ? "Change AWB" : "Add AWB"} — ${shipmentName(shipment)}`
  return <AfterShipmentDialog id="logistics-awb-title" title={title} dirty={Boolean(typed || link.trim() || reason.trim())} failure={failure} onClose={onClose}
    hint={changing ? reasonHint(reason, "change the AWB") : null}
    actions={<Button color="primary" disabled={pending || !ready} onClick={submit}>{pending ? "Saving…" : "Save AWB"}</Button>}>
    {changing && <p className="small text-muted">{`Now: ${transportName(shipment)} · AWB ${shipment.awb} · ${known === shipment.awb ? "the organizer has it"
      : "not sent to the organizer yet"}`}</p>}
    <AwbFields courier={shipment.courier} id="logistics-awb" label={changing ? "New AWB" : "AWB"} awb={awb} link={link} onAwb={setAwb} onLink={setLink}
      required />
    {changing && <ReasonField id="logistics-awb-reason" value={reason} onChange={setReason} placeholder="e.g. the first label tore" />}
    <p className="small mt-3 mb-0">{known && known !== typed
      ? `${receivers(shipment) || "The contact person and the organizer receive"} the short „New AWB” email: ${typed || "the new AWB"} replaces ${known}.`
      : "No email now: the organizer gets the AWB with the processing."}</p>
  </AfterShipmentDialog>
}

// What Mark as shipped says of the email: none, since the organizer has the AWB and the tracking link already (D18), and Cargus writes to
// the contact person itself.
const noEmail = shipment => (shipment.transport !== "courier" ? "No email."
  : `No email: the organizer already has the AWB and the tracking link${shipment.courier === "cargus" ? ", and Cargus notifies the contact person" : ""}.`)

// „Mark as shipped” (D15, D19): the day the courier picked the parcels up, or the goods were handed over without one, today by default and
// never later. Only now does the content leave the stock: each product and place, two lines of one product added up, with the stock it
// leaves (`balances`, D22); before the initial inventory, nothing moves, and a supplementary shipment's text moves nothing either.
export function MarkShippedDialog({ shipment, balances, pending, failure, onSubmit, onClose }) {
  const today = todayIn(() => new Date())
  const [date, setDate] = useState(today)
  const valid = /^\d{4}-\d{2}-\d{2}$/.test(date) && date <= today
  const items = sentItems(shipment)
  const stockAfter = item => (balances?.[itemKey(item)] == null ? "—" : quantityLabel(balances[itemKey(item)] - item.quantity, item.unit))
  return <AfterShipmentDialog id="logistics-ship-title" title={`Mark as shipped — ${shipmentName(shipment)}`} size="lg" dirty={date !== today} failure={failure}
    onClose={onClose} actions={<Button color="primary" disabled={pending || !valid} onClick={() => onSubmit({ action: "markShipped", date })}>
      {pending ? "Saving…" : "Mark as shipped"}</Button>}>
    <p>{[transportLabel(shipment), weightLabel(shipment.weight)].filter(Boolean).join(" · ")}</p>
    <FormGroup className="logistics-day">
      <Label for="logistics-ship-day">{shipment.transport === "courier" ? "Picked up on" : "Handed over on"}</Label>
      <DateTimeField id="logistics-ship-day" mode="date" max={today} value={date} invalid={!valid} onChange={event => setDate(event.target.value)} />
      {valid ? <FormText>Today or earlier, never later.</FormText> : <FormFeedback>Choose today or an earlier day.</FormFeedback>}
    </FormGroup>
    {!balances ? <p className="small">Before the initial inventory: the stock does not change.</p>
      : items.length ? <>
        <p className="mb-1"><strong>Taken from the stock</strong></p>
        <Table size="sm" className="logistics-stack">
          <thead><tr><th>Product</th><th className="text-right">Quantity</th><th className="text-right">Stock after</th></tr></thead>
          <tbody>{items.map(item => <tr key={itemKey(item)}>
            <td>{itemName(item)}</td>
            <td className="text-right" data-label="Quantity">{quantityLabel(item.quantity, item.unit)}</td>
            <td className="text-right" data-label="Stock after">{stockAfter(item)}</td>
          </tr>)}</tbody>
        </Table>
      </> : <p className="small">Only a text: the stock does not change.</p>}
    <p className="small text-muted mb-0">{noEmail(shipment)}</p>
  </AfterShipmentDialog>
}

// „Undo processing” (D17): back to before the processing, with a reason; the AWB and the parcels stay, and no email goes. An AWB Cargus
// generated has no Change AWB: only another generation replaces it.
export function UndoProcessingDialog({ shipment, pending, failure, onSubmit, onClose }) {
  const [reason, setReason] = useState("")
  const back = `Back to ${unprocessedName(shipment)}.`
  return <AfterShipmentDialog id="logistics-undo-title" title={`Undo processing — ${shipmentName(shipment)}`} dirty={Boolean(reason.trim())} failure={failure}
    onClose={onClose} hint={reasonHint(reason, "undo the processing")} actions={<Button color="warning" disabled={pending || !reason.trim()}
      onClick={() => onSubmit({ action: "undoProcessing", reason: reason.trim() })}>{pending ? "Undoing…" : "Undo processing"}</Button>}>
    <p>{!shipment.awb ? `${back} No email.`
      : shipment.awbSource === "generated" ? `${back} AWB ${shipment.awb} and its ${plural(shipment.parcels, "parcel")}, generated through Cargus, `
        + "stay; to change them, generate another AWB. No email."
        : `${back} AWB ${shipment.awb} stays; change it with Change AWB if it is wrong. No email.`}</p>
    <ReasonField id="logistics-undo-reason" value={reason} onChange={setReason} placeholder="e.g. one more box" />
  </AfterShipmentDialog>
}
